'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import logTrailDetailsConditionsCTATapped from '@alltrails/amplitude/events/logTrailDetailsConditionsCTATapped';
import { useGetTrailWeatherConditionsQuery } from '@alltrails/conditions/redux/conditionsApi';
import TrailConditionKey from '@alltrails/conditions/types/TrailConditionKey';
import TrailWeatherOverviewBackgroundTheme from '@alltrails/conditions/types/TrailWeatherOverviewBackgroundTheme';
import ShimmerContainer from '@alltrails/core/components/ShimmerContainer';
import { IconComponent } from '@alltrails/denali/components/Icon';
import IconRenderer from '@alltrails/denali/components/IconRenderer';
import PeakLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Peak';
import PlusLogoBadge from '@alltrails/denali/components/SubscriptionBadge/Logo/Plus';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import ChevronSm from '@alltrails/denali/icons/ChevronSm';
import River from '@alltrails/denali/icons/River';
import Snowflake from '@alltrails/denali/icons/Snowflake';
import TrailGravel from '@alltrails/denali/icons/TrailGravel';
import TrailIcy from '@alltrails/denali/icons/TrailIcy';
import TrailMuddy from '@alltrails/denali/icons/TrailMuddy';
import TrailPartiallyPaved from '@alltrails/denali/icons/TrailPartiallyPaved';
import Uv from '@alltrails/denali/icons/Uv';
import Waves from '@alltrails/denali/icons/Waves';
import Hurricane from '@alltrails/denali/icons/Weather1';
import Wind from '@alltrails/denali/icons/Wind';
import { useExperiments } from '@alltrails/experiments';
import { updateDynamicStyleIds } from '@alltrails/maps/redux/reducer';
import { ConditionStyleId } from '@alltrails/maps/types/Styles';
import { useAppDispatch, useAppSelector } from '@/utils/redux';
import { setIsTrailMapOpen } from '@/utils/trailPageSlice';
import TrailConditionsErrorCard from '../TrailConditionsErrorCard/TrailConditionsErrorCard';
import TrailConditionsWeatherMapButton from '../TrailConditionsWeatherMapButton/TrailConditionsWeatherMapButton';
import styles from './TrailConditionsCard.module.scss';

const TrailConditionsInstallInterstitial = dynamic(() => import('../TrailConditionsInstallInterstitial/TrailConditionsInstallInterstitial'), {
  ssr: false
});

type TrailConditionsCardProps = {
  localizedDateString?: string;
  theme?: TrailWeatherOverviewBackgroundTheme;
  trailId: number;
};

const TrailConditionsCard = ({ localizedDateString, theme, trailId }: TrailConditionsCardProps) => {
  const dispatch = useAppDispatch();
  const isMobile = useIsMobileSizedScreen();
  const displayMetric = useAppSelector(state => state.context.displayMetric);
  const experiments = useExperiments();
  const showGroundConditions = experiments['web-disco-rm-ground-conditions']?.value === 'treatment';

  const [showInstallInterstitial, setShowInstallInterstitial] = useState(false);

  const { data, isError, isFetching, isLoading, isUninitialized, refetch } = useGetTrailWeatherConditionsQuery(
    {
      date: localizedDateString || '',
      sentenceUnits: displayMetric ? 'm' : 'i',
      trailId
    },
    { skip: !localizedDateString }
  );

  const renderIcon = useCallback(
    (Component: IconComponent) => {
      return <IconRenderer defaults={{ color: 'currentColor', size: isMobile ? 'sm' : 'lg' }} icon={{ Component }} />;
    },
    [isMobile]
  );

  if (isError) {
    return (
      <TrailConditionsErrorCard className={styles.errorCard} onRetry={refetch} showIllustration trailId={trailId} />
    );
  }
  if (isFetching || isLoading || isUninitialized || !data || !theme) {
    return <ShimmerContainer className={classNames(styles.container, styles.loading)} />;
  }

  const renderTerrainIcon = () => {
    let icon = TrailPartiallyPaved;
    switch (data.terrain.conditionKey) {
      case 'dry':
      case 'dry_dusty':
        icon = TrailGravel;
        break;
      case 'icy':
        icon = TrailIcy;
        break;
      case 'muddy':
      case 'muddy_icy':
        icon = TrailMuddy;
        break;
      case 'snowy':
      case 'snowy_icy':
        icon = Snowflake;
        break;
      case 'wet':
        icon = River;
        break;
      default:
        break;
    }
    return renderIcon(icon);
  };

  const renderWindIcon = () => {
    let icon = Wind;
    switch (data.wind.conditionKey) {
      case 'calm':
      case 'light_air':
      case 'light_breeze':
      case 'gentle_breeze':
        icon = Waves;
        break;
      case 'hurricane_force':
        icon = Hurricane;
        break;
      default:
        break;
    }
    return renderIcon(icon);
  };

  const condition: ConditionStyleId = theme.includes('rainy') ? 'precipitation' : 'temperature';

  const onWeatherMapButtonClick = () => {
    logTrailDetailsConditionsCTATapped({
      action_taken: 'cta tapped',
      cta_type: 'weather map',
      trail_id: trailId
    });

    if (isMobile) {
      setShowInstallInterstitial(true);
    } else {
      dispatch(setIsTrailMapOpen(true));
      dispatch(updateDynamicStyleIds([condition]));
    }
  };

  const onTerrainButtonClick = () => {
    logTrailDetailsConditionsCTATapped({
      action_taken: 'cta tapped',
      cta_type: 'ground conditions',
      trail_id: trailId
    });

    if (isMobile) {
      setShowInstallInterstitial(true);
    } else {
      dispatch(setIsTrailMapOpen(true));
      if ((['snowy', 'snowy_icy', 'icy', 'muddy_icy'] as TrailConditionKey[]).includes(data.terrain.conditionKey)) {
        dispatch(updateDynamicStyleIds(['snowDepth']));
      } else {
        dispatch(updateDynamicStyleIds(['groundConditions']));
      }
    }
  };

  const terrainDataItem = showGroundConditions ? (
    <button className={styles.dataItem} onClick={onTerrainButtonClick}>
      {renderTerrainIcon()}
      <div className={styles.dataText}>
        <div className={styles.title}>
          {data.terrain.title}
          <ChevronSm color="currentColor" orientation="right" size={isMobile ? 'sm' : 'md'} />
        </div>
        <div className={styles.description}>{data.terrain.description}</div>
      </div>
    </button>
  ) : (
    <div className={styles.dataItem}>
      {renderTerrainIcon()}
      <div className={styles.dataText}>
        <div className={styles.title}>{data.terrain.title}</div>
        <div className={styles.description}>{data.terrain.description}</div>
      </div>
    </div>
  );

  return (
    <>
      <div className={classNames(styles.container, styles[theme])}>
        <div className={styles.mainContent}>
          <div className={styles.heading}>
            <FormattedMessage defaultMessage="Trail conditions" />
            {experiments['rm-conditions-plus']?.value === 'on' ? (
              <PlusLogoBadge size={isMobile ? 'sm' : 'md'} textColor={theme.includes('night') ? 'light' : 'dark'} variant="full" />
            ) : (
              <PeakLogoBadge size={isMobile ? 'sm' : 'md'} textColor={theme.includes('night') ? 'light' : 'dark'} variant="full" />
            )}
          </div>
          <div className={styles.data}>
            {terrainDataItem}
            <div className={styles.dataItem}>
              {renderWindIcon()}
              <div className={styles.dataText}>
                <div className={styles.title}>{data.wind.title}</div>
                <div className={styles.description}>{data.wind.description}</div>
              </div>
            </div>
            <div className={styles.dataItem}>
              {renderIcon(Uv)}
              <div className={styles.dataText}>
                <div className={styles.title}>{data.uv.title}</div>
                <div className={styles.description}>{data.uv.description}</div>
              </div>
            </div>
          </div>
        </div>
        <TrailConditionsWeatherMapButton condition={condition} onClick={onWeatherMapButtonClick} />
      </div>
      <TrailConditionsInstallInterstitial
        isOpen={showInstallInterstitial}
        onRequestClose={() => setShowInstallInterstitial(false)}
        trailId={trailId}
      />
    </>
  );
};

export default TrailConditionsCard;
